import { AnyObject } from '@store/types';
import { useEffect, useState } from 'react';
import flatten from 'lodash/flatten';
import compact from 'lodash/compact';

import { Nullable } from 'chaika-common-types';
import { isDefined } from '@utils/isDefined';
import { ActionTypes, TableField, TableFieldType } from '@components/Table/CreationMenu';

interface Props {
    fields: TableField<Nullable<string>>[];
    onSaveClick?(params: any): void | Promise<void>;
    onCancelClick?(): void;
}

interface Data {
    menuData: object;
    isAllValid: boolean;
    isSaving: boolean;
    setValidStatus(value: boolean): void;
    setMenuDataValues(params: object): Promise<void>;
    onButtonSaveClick(): Promise<void>;
    onCancelButtonClick(): void;
}

export function useCreationMenu({ fields, onSaveClick, onCancelClick }: Props): Data {
    const [menuData, setMenuData] = useState<AnyObject>({});
    const [isAllValid, setIsAllValid] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        const objFields: AnyObject = {};
        fields.forEach(item => {
            if (!item.isNotUpdated) {
                objFields[item.fieldName] = item.value;
            }
            item.multiFields?.forEach(multiField => {
                if (!multiField.isNotUpdated) {
                    objFields[multiField.fieldName] = multiField.value;
                }
            });
        });

        setMenuData(objFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        function validateField(field: TableField<Nullable<string>>): boolean {
            if (field.type === TableFieldType.MultiField && Array.isArray(field.multiFields)) {
                return field.multiFields.every(validateField);
            }

            if (field.validationParams?.validationFunction) {
                return field.validationParams.validationFunction(menuData[field.fieldName]);
            }

            return true;
        }

        const valid = fields.every(validateField);
        setIsAllValid(valid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuData]);

    async function setMenuDataValues(params: object): Promise<void> {
        const field = findUpdatedField(params);
        const data = { ...menuData, ...params };

        const fieldsWhoHasDepByCurrentField = findDependentFields(field);
        const updatedData = updateDependentFieldsValues(fieldsWhoHasDepByCurrentField, data);

        setMenuData(prev => ({
            ...prev,
            ...updatedData,
        }));

        if (field?.onChangeValue) {
            await field.onChangeValue(updatedData);
        }
    }

    function findUpdatedField(params: object) {
        return fields.find(field => Object.keys(params).some(key => key === field.fieldName));
    }

    function findDependentFields(field?: TableField<Nullable<string>>): TableField<Nullable<string>>[] {
        const withoutMultiFields = fields.filter(it => it.type !== TableFieldType.MultiField);
        const multiFieldsFields = compact(
            flatten(fields.filter(f => f.type === TableFieldType.MultiField).map(it => it.multiFields)),
        );

        return [...withoutMultiFields, ...multiFieldsFields].filter(fieldItem =>
            fieldItem.depByFields?.dependencies?.some(item => item.field === field?.fieldName),
        );
    }

    function updateDependentFieldsValues(
        fieldsWhoHasDep: TableField<Nullable<string>>[],
        data: Record<string, any>,
    ): Record<string, any> {
        const newData = { ...data };

        fieldsWhoHasDep.forEach(fieldParams => {
            const isValueCalculated = !!fieldParams.depByFields?.actionType.includes(ActionTypes.CalculateValueByField);
            const valueData = isValueCalculated
                ? fieldParams.depByFields?.dependencies
                      ?.find(item => isDefined(item?.calculateValue))
                      ?.calculateValue?.(newData)
                : undefined;

            if (valueData) {
                newData[fieldParams.fieldName] = valueData;
            }
        });

        return newData;
    }

    async function onButtonSaveClick(): Promise<void> {
        setIsSaving(true);

        try {
            await onSaveClick?.(menuData);
        } finally {
            setIsSaving(false);
        }
    }

    function onCancelButtonClick(): void {
        onCancelClick?.();
    }

    function setValidStatus(value: boolean): void {
        setIsAllValid(value);
    }

    return {
        menuData,
        isAllValid,
        isSaving,
        setValidStatus,
        setMenuDataValues,
        onButtonSaveClick,
        onCancelButtonClick,
    };
}
